<script setup lang="ts">
import { useSiteStore } from '~/stores/site.store';

interface ICarouselProps {
  title?: string;
  id: string;
  route?: string;
  displayShowAll?: boolean;
  verticalNavigation?: boolean;
  titleClass?: string;
  titleContainerClass?: string;
  carouselClass?: string;
  displayArrows?: boolean;
  displayTitle?: boolean;
  gutter?: boolean;
  background?: boolean;
  steps?: boolean;
}

const props = withDefaults(defineProps<ICarouselProps>(), {
  title: '',
  route: '',
  displayShowAll: true,
  verticalNavigation: false,
  titleClass: 'text-base text-base-color',
  titleContainerClass: '',
  carouselClass: '',
  displayArrows: true,
  displayTitle: true,
  gutter: true,
  background: false,
  steps: false,
});

const carouselId = computed<string>(() => `${props.id.toLowerCase()}-carousel`);

const site = useSiteStore();
const viewport = useViewport();

const displayTooltip = ref<boolean>(
  site.getFeatureFlags.get('website.enabletitletooltips'),
);

const scroller = ref<HTMLElement>();
const isDown = ref(false);
const hasScrolled = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const scrollPosition = ref('0%');

const dragStart = (event: MouseEvent) => {
  if (scroller.value) {
    hasScrolled.value = false;
    isDown.value = true;
    startX.value = event.pageX - scroller.value.offsetLeft;
    scrollLeft.value = scroller.value.scrollLeft;
    if (viewport.isGreaterThan('tablet')) {
      setTimeout(() => {
        scroller.value?.childNodes?.forEach((cn) => {
          (cn as HTMLElement)?.classList?.add('pointer-events-none');
        });
      }, 250);
      setTimeout(() => {
        if (scrollLeft.value === scroller.value?.scrollLeft) dragEnd('mouseup');
      }, 750);
    }
  }
};

const dragEnd = () => {
  isDown.value = false;
  if (scroller.value && viewport.isGreaterThan('tablet')) {
    scroller.value?.childNodes?.forEach((cn) => {
      (cn as HTMLElement)?.classList?.remove('pointer-events-none');
    });
  }
};

const handleScroll = (event: MouseEvent) => {
  if (isDown.value && scroller.value) {
    event.preventDefault();
    hasScrolled.value = true;
    const x = event.pageX - scroller.value.offsetLeft;
    const walk = x - startX.value;
    scroller.value.scrollLeft = scrollLeft?.value - walk;
  }
};

const calculateScrollPosition = () => {
  if (scroller.value) {
    scrollPosition.value =
      ((scroller.value?.scrollLeft +
        scroller.value?.getBoundingClientRect().width) /
        scroller.value?.scrollWidth) *
        100 +
      '%';
  } else scrollPosition.value = '0%';
};

onMounted(() => calculateScrollPosition());
</script>

<template>
  <div
    :class="[
      'container md:mx-auto mx-0 px-0 cursor-grab relative',
      { 'cursor-grabbing': isDown },
    ]"
  >
    <div
      v-if="displayTitle"
      class="text-base text-base-priority flex justify-between items-center pb-2 pt-7 md:mx-0"
      :class="[props.titleContainerClass]"
    >
      <div
        class="font-bold truncate capitalize flex items-center"
        :class="props.titleClass"
      >
        <slot name="icon" />
        {{ props.title }}
        <slot name="subtitle" />
        <LazyDesignSystemTooltipModal
          v-if="displayTooltip"
          :title="props.title"
          :route="props.route"
        />
      </div>
      <div class="gap-1 items-center flex">
        <NuxtLink v-if="props.route && props.displayShowAll" :to="route">
          <DesignSystemButton
            :aria-label="
              !props.verticalNavigation ? $t('show-all') : $t('all-games')
            "
            class="text-sm gap-1"
            type="transparent"
            padding="sm"
          >
            {{ !props.verticalNavigation ? $t('show-all') : $t('all-games') }}
            <slot name="prefix-arrows" />
          </DesignSystemButton>
        </NuxtLink>
        <slot name="suffix-arrows" />
      </div>
    </div>
    <div
      :class="{
        'dark:bg-dark-700 bg-light-50 rounded-xl p-2 lg:w-fit':
          props?.background,
      }"
    >
      <div
        :id="carouselId"
        ref="scroller"
        :class="[
          'scroller-casino flex flex-shrink-0 overflow-x-scroll overflow-y-hidden scrollbar-none',
          props?.background ? 'w-auto' : 'w-full mb-2',
          isDown ? 'cursor-grabbing' : 'cursor-grab',
          gutter ? 'gap-2 md:gap-4' : '',
          carouselClass,
          !!$attrs?.class && $attrs?.class,
        ]"
        @mousedown="dragStart"
        @mouseup="dragEnd('mouseup')"
        @mouseleave="dragEnd('mouseleave')"
        @mousemove="handleScroll"
        @scroll="calculateScrollPosition"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
::selection {
  background: transparent;
}
::-moz-selection {
  background: transparent;
}
</style>
